import React, { useState, useEffect } from 'react';
import { User, Mail, Shield, Lock, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from "react-i18next";
import './UserForm.css';

/**
 * UserForm Component
 * Handles both Add and Edit user operations
 * Mode-driven behavior (composition pattern)
 */
export default function UserForm({ 
  mode = 'add', // 'add' or 'edit'
  user = null,
  allowedRoles = ['promoter', 'admin'],
  roleLocked = false,
  onSubmit,
  onCancel,
}) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    role: 'promoter',
    is_active: true,
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const roleOptions = [
    { value: 'promoter', label: t("admin.userForm.promoter") },
    { value: 'admin', label: t("admin.userForm.admin") },
    { value: 'op', label: t("admin.userForm.op", { defaultValue: "OP" }) },
  ].filter((option) => allowedRoles.includes(option.value));

  // Initialize form with user data in edit mode
  useEffect(() => {
    if (mode === 'edit' && user) {
      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
        role: user.role || 'promoter',
        is_active: user.is_active !== undefined ? user.is_active : true,
        password: '',
        confirmPassword: '',
      });
    }
  }, [mode, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Name validation
    if (!formData.full_name.trim()) {
      newErrors.full_name = t("admin.userForm.validation.fullNameRequired");
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = t("admin.userForm.validation.emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("admin.userForm.validation.emailInvalid");
    }

    // Password validation (only for add mode or if password is entered in edit mode)
    if (mode === 'add') {
      if (!formData.password) {
        newErrors.password = t("admin.userForm.validation.passwordRequired");
      } else if (formData.password.length < 6) {
        newErrors.password = t("admin.userForm.validation.passwordMin");
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = t("admin.userForm.validation.passwordMismatch");
      }
    } else if (mode === 'edit' && formData.password) {
      // In edit mode, only validate password if it's being changed
      if (formData.password.length < 6) {
        newErrors.password = t("admin.userForm.validation.passwordMin");
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = t("admin.userForm.validation.passwordMismatch");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data - exclude password and confirmPassword fields if empty in edit mode
      const submitData = {
        full_name: formData.full_name,
        email: formData.email,
        role: formData.role,
        is_active: formData.is_active,
      };

      // Include password only if it's provided
      if (mode === 'add' || (mode === 'edit' && formData.password)) {
        submitData.password = formData.password;
      }

      await onSubmit(submitData);
    } catch (error) {
      // Error is now handled by the notification system at the parent level
      // No need to display it in the form
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="user-form" onSubmit={handleSubmit}>
      <div className="user-form-header">
        <div className="user-form-icon">
          {mode === 'add' ? <User size={24} /> : <Shield size={24} />}
        </div>
        <div>
          <h2 className="user-form-title">
            {mode === 'add' ? t("admin.userForm.addNewUser") : t("admin.userForm.editUser")}
          </h2>
          <p className="user-form-subtitle">
            {mode === 'add' 
              ? t("admin.userForm.createSubtitle")
              : t("admin.userForm.updateSubtitle")}
          </p>
        </div>
      </div>

      <div className="user-form-body">
        {/* Personal Information Section */}
        <div className="user-form-section">
          <h3 className="user-form-section-title">{t("admin.userForm.personalInfo")}</h3>
          
          <div className="user-form-group">
            <label className="user-form-label" htmlFor="full_name">
            {t("admin.userForm.fullName")}<span className="required-asterisk">*</span>
            </label>


            <div className="user-form-input-wrapper">
              <User className="user-form-input-icon" size={18} />
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className={`user-form-input ${errors.full_name ? 'input-error' : ''}`}
                placeholder={t("admin.userForm.enterFullName")}
              />
            </div>
            {errors.full_name && (
              <span className="form-error-text">{errors.full_name}</span>
            )}
          </div>

          <div className="user-form-group">
            <label className="user-form-label" htmlFor="email">
            {t("admin.userForm.emailAddress")}<span className="required-asterisk">*</span>
            </label>

            <div className="user-form-input-wrapper">
              <Mail className="user-form-input-icon" size={18} />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`user-form-input ${errors.email ? 'input-error' : ''}`}
                placeholder={t("admin.userForm.emailPlaceholder")}
                disabled={mode === 'edit'}
              />
            </div>
            {errors.email && (
              <span className="form-error-text">{errors.email}</span>
            )}
            {mode === 'edit' && (
              <span className="form-help-text">📧 {t("admin.userForm.emailCannotChange")}</span>
            )}
          </div>
        </div>

        {/* Account Settings Section */}
        <div className="user-form-section">
          <h3 className="user-form-section-title">{t("admin.userForm.accountSettings")}</h3>
          
          <div className="user-form-group">
            <label className="user-form-label" htmlFor="role">
            {t("admin.userForm.userRole")}<span className="required-asterisk">*</span>
            </label>

            <div className="user-form-input-wrapper">
              <Shield className="user-form-input-icon" size={18} />
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="user-form-select"
                disabled={roleLocked}
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {roleLocked && (
              <span className="form-help-text">{t("admin.userForm.roleLocked")}</span>
            )}
          </div>

          <div className="user-form-group">
            <label className="user-form-label">
            {t("admin.userForm.accountStatus")}<span className="required-asterisk">*</span>
            </label>

            <div className="user-form-status-group">
              <div className="user-form-status-options">
                <div className="user-form-status-option">
                  <input
                    type="radio"
                    id="status-inactive"
                    name="is_active"
                    value="false"
                    checked={!formData.is_active}
                    onChange={() => setFormData(prev => ({ ...prev, is_active: false }))}
                  />
                  <label htmlFor="status-inactive" className="user-form-status-label">
                    <span>{t("admin.userForm.disabled")}</span>
                  </label>
                </div>
                <div className="user-form-status-option">
                  <input
                    type="radio"
                    id="status-active"
                    name="is_active"
                    value="true"
                    checked={formData.is_active}
                    onChange={() => setFormData(prev => ({ ...prev, is_active: true }))}
                  />
                  <label htmlFor="status-active" className="user-form-status-label">
                    <span>{t("admin.userForm.active")}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Security Section */}
        <div className="user-form-section">
          <h3 className="user-form-section-title">{t("admin.userForm.security")}</h3>
          
          <div className="user-form-group">
            <label className="user-form-label" htmlFor="password">
            {mode === 'edit' ? t("admin.userForm.passwordLeaveBlank") : t("admin.userForm.password")}
            {mode === 'add' && (
                <span className="required-asterisk" aria-hidden="true">*</span>
            )}
            </label>

            <div className="user-form-input-wrapper">
              <Lock className="user-form-input-icon" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`user-form-input ${errors.password ? 'input-error' : ''}`}
                placeholder={mode === 'add' ? t("admin.userForm.enterPassword") : t("admin.userForm.enterNewPassword")}
              />
              <button
                type="button"
                className="user-form-toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && (
              <span className="form-error-text">{errors.password}</span>
            )}
          </div>

          <div className="user-form-group">
           <label className="user-form-label" htmlFor="confirmPassword">
            {t("admin.userForm.confirmPassword")}
            {mode === 'add' && (
                <span className="required-asterisk" aria-hidden="true">*</span>
            )}
            </label>

            <div className="user-form-input-wrapper">
              <Lock className="user-form-input-icon" size={18} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`user-form-input ${errors.confirmPassword ? 'input-error' : ''}`}
                placeholder={t("admin.userForm.confirmPasswordPlaceholder")}
              />
              <button
                type="button"
                className="user-form-toggle-password"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="form-error-text">{errors.confirmPassword}</span>
            )}
          </div>
        </div>
      </div>

      <div className="user-form-footer">
        <button
          type="button"
          className="user-form-btn user-form-btn-cancel"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t("admin.userForm.cancel")}
        </button>
        <button
          type="submit"
          className="user-form-btn user-form-btn-submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="btn-spinner"></span>
              {t("admin.userForm.saving")}
            </>
          ) : (
            <>
              {mode === 'add' ? t("admin.userForm.addUser") : t("admin.userForm.saveChanges")}
            </>
          )}
        </button>
      </div>
    </form>
  );
}
